import {Box} from '@mui/material';
import {DailyCareLogFormFooterButtons} from '../../../../components/daily-care-log/handle-task-form/daily-care-log-form-footer-buttons';
import {translations} from '../../../../i18n/translations';
import {useTranslation} from 'react-i18next';
import {useEffect, useState} from 'react';
import {useHistory, useParams, useLocation} from 'react-router-dom';
import {ScreenPaths} from '../../../../enums';
import {useDailyCareLog} from '../../../../contexts/daily-care-log/context';
import {DailyCareLogModal} from '../../../../components/daily-care-log/handle-task-form/daily-care-modal';
import {AffiliatedUser} from '../../../../backend/responses/affiliated-user';
import {AddUnplannedTaskForm, AddUnplannedTaskFormData} from './add-unplannedtask-form';
import './unplannedtask.daily-care-log.screen.scss';
import {useSnackBar} from '../../../../contexts/snack-bar/context';
import {PostStorageRequest} from '../../../../backend/requests/post-storage-request';
import {StorageType} from '../../../../backend/enums';
import {StorageRequestRepository} from '../../../../repositories/storage-request';
import {
  CareTaskAttachmentStorageRequestData,
  StorageRequestResult,
} from '../../../../backend/interfaces/storage-request';
import {EntityUserCareTasksRepository} from '../../../../repositories/entity-user-care-tasks';
import moment from 'moment';
import {Attachment} from '../../../../backend/interfaces/attachment';
import {AttachmentType} from '../../../../backend/enums/attachment-type';
import {SavingAttachment} from '../../../../components/daily-care-log/attachments/add-attachments-form';

type UnplannedTaskDailyCareLogScreenProps = {
  users?: AffiliatedUser[];
};

export const UnplannedTaskDailyCareLogScreen = ({users}: UnplannedTaskDailyCareLogScreenProps) => {
  const {t} = useTranslation();
  const {entityId, entityShiftId}: {entityId: string; entityShiftId: string} = useParams();
  let history = useHistory();
  const location = useLocation();
  const defaultPatientId = new URLSearchParams(location.search).get('userId') || undefined;
  const dailyCareLogContext = useDailyCareLog();
  const snackBarContext = useSnackBar();
  const [isFormValid, setIsFormValid] = useState(false);
  const [formData, setFormData] = useState<AddUnplannedTaskFormData | undefined>(undefined);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [patientsSavingStatus, setPatientsSavingStatus] = useState<{
    [id: string]: 'error' | 'success';
  }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    dailyCareLogContext.setNavigationHeader(
      true,
      t(translations.daily_care_log.shift_details.unplanned_tasks.header_title)
    );
    dailyCareLogContext.toggleBottomNavigation(false);

    return () => {
      dailyCareLogContext.setNavigationHeader(false);
      dailyCareLogContext.toggleBottomNavigation(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const backToTaskDetails = () => {
    if (location.state) {
      history.push(
        `${ScreenPaths.DailyCareLog}/${entityId}${ScreenPaths.DailyCareLog_Shift}/${entityShiftId}?date=${location.state}`
      );
    } else {
      history.push(`${ScreenPaths.DailyCareLog}/${entityId}${ScreenPaths.DailyCareLog_Home}`);
    }
  };

  const handleChangeFormChange = (values: AddUnplannedTaskFormData, errors: boolean) => {
    setFormData(values);

    if (
      values.activitySector &&
      values.activityGroup &&
      values.activityType &&
      values.date &&
      values.time &&
      values.patients.length > 0 &&
      (values.markedAsDone === 'done' || !!values.notDoneReason) &&
      !errors
    ) {
      setIsFormValid(true);
    } else {
      setIsFormValid(false);
    }
  };

  const handleNewUnplannedTask = async () => {
    if (!formData || !entityId) return;
    setIsSubmitting(true);
    await saveUnplannedTasks(formData);

    const findError = Object.values(patientsSavingStatus).find((status) => status === 'error');
    setIsSubmitting(false);

    if (!findError) {
      snackBarContext.setDailyCareLogToastNotification({
        severity: 'success',
        message:
          formData.patients.length > 1
            ? t(
                translations.daily_care_log.shift_details.unplanned_tasks.add_multiple_success_toast
              )
            : t(translations.daily_care_log.shift_details.unplanned_tasks.add_success_toast),
      });
      setPatientsSavingStatus({});
      backToTaskDetails();
    }
  };

  const saveUnplannedTasks = async (formData: AddUnplannedTaskFormData) => {
    let attachments: Attachment[] = [];
    let storageError = false;

    if (formData.files) {
      const result = await requestAttachmentStorageUrl(
        entityId,
        formData.files,
        StorageType.CareTaskAttachment
      );
      if (result === null) {
        snackBarContext.setDailyCareLogToastNotification({
          severity: 'error',
          message: t(translations.daily_care_log.shift_details.unplanned_tasks.upload_error_toast),
        });
        storageError = true;
      } else {
        attachments = result;
      }
    }
    if (storageError) {
      setPatientsSavingStatus((prevState) => ({
        ...prevState,
        [formData.patients[0]]: 'error',
      }));
      return Promise.reject();
    }
    for (const patient of formData.patients) {
      if (patientsSavingStatus[patient] === 'success') continue;
      if (storageError) {
        setPatientsSavingStatus((prevState) => ({
          ...prevState,
          [patient]: 'error',
        }));
        return;
      }
      try {
        await EntityUserCareTasksRepository.instance.AddUnplannedTask(
          entityId,
          patient,
          new Date(
            moment(formData.date).year(),
            moment(formData.date).month(),
            moment(formData.date).date(),
            moment(formData.time).hours(),
            moment(formData.time).minutes(),
            moment(formData.time).seconds()
          ),
          formData.activitySector,
          formData.activityGroup,
          formData.activityType,
          formData.markedAsDone === 'done' ? true : false,
          formData.details || undefined,
          [entityShiftId],
          formData.notDoneReason,
          formData.data,
          formData.feature,
          attachments,
          formData.nameConfigurationValue
        );
        setPatientsSavingStatus((prevState) => ({
          ...prevState,
          [patient]: 'success',
        }));
      } catch (e) {
        setPatientsSavingStatus((prevState) => ({
          ...prevState,
          [patient]: 'error',
        }));
        snackBarContext.setDailyCareLogToastNotification({
          severity: 'error',
          message: t(translations.daily_care_log.shift_details.unplanned_tasks.add_error_toast),
        });
        console.error(e);
      }
    }
  };

  return (
    <Box className="add-unplanned-form">
      <AddUnplannedTaskForm
        entityId={entityId}
        users={users}
        defaultPatientId={defaultPatientId}
        onFormChange={handleChangeFormChange}
      />
      <DailyCareLogFormFooterButtons
        canWrite
        editMode
        isCancelDisabled={isSubmitting}
        isSaveDisabled={!isFormValid || isSubmitting}
        isSaveLoading={isSubmitting}
        saveLabel={t(translations.daily_care_log.task_details.register)}
        onCancel={() => {
          if (
            formData &&
            (formData.activitySector !== '' ||
              formData.activityGroup !== '' ||
              formData.activityType !== '' ||
              formData.patients.length > 0)
          ) {
            setIsCancelModalOpen(true);
          } else {
            backToTaskDetails();
          }
        }}
        onSave={handleNewUnplannedTask}
        onEdit={() => {}}
      />
      <DailyCareLogModal
        open={isCancelModalOpen}
        title={t(translations.daily_care_log.shift_details.unplanned_tasks.add_cancel_modal_title)}
        subtitle={t(
          translations.daily_care_log.shift_details.unplanned_tasks.add_cancel_modal_subtitle
        )}
        onClose={() => setIsCancelModalOpen(false)}
        onConfirm={async () => {
          if (location.state) {
            history.push(
              `${ScreenPaths.DailyCareLog}/${entityId}${ScreenPaths.DailyCareLog_Shift}/${entityShiftId}?date=${location.state}`
            );
          } else {
            history.push(`${ScreenPaths.DailyCareLog}/${entityId}${ScreenPaths.DailyCareLog_Home}`);
          }
        }}
      />
    </Box>
  );
};

export async function requestAttachmentStorageUrl(
  entityId: string,
  files: SavingAttachment[],
  type: StorageType.CareTaskAttachment | StorageType.OccurrenceAttachment,
  authorUserId?: string
): Promise<Attachment[] | null> {
  var promises = files.map(async (file) => {
    if (file.storageUrl) {
      return {
        type: file.attachmentType as AttachmentType,
        name: file.file.name,
        sizeInMb: file.file.size / (1024 * 1024),
        authorUserId: authorUserId,
        data: {fileUrl: file.storageUrl},
      };
    }
    const requestAttachmentBody: PostStorageRequest<CareTaskAttachmentStorageRequestData> = {
      type: type,
      fileType: file.storageType,
      data: {entityId: entityId},
    };
    const storageAttachmentRequestResult: StorageRequestResult<CareTaskAttachmentStorageRequestData> =
      await StorageRequestRepository.instance.create<CareTaskAttachmentStorageRequestData>(
        requestAttachmentBody
      );
    const attachmentUrl =
      await StorageRequestRepository.instance.upload<CareTaskAttachmentStorageRequestData>(
        storageAttachmentRequestResult,
        file.file
      );
    return {
      type: file.attachmentType as AttachmentType,
      name: file.file.name,
      sizeInMb: file.file.size / (1024 * 1024),
      authorUserId: authorUserId,
      data: {fileUrl: attachmentUrl},
    };
  });

  try {
    const response = await Promise.all(promises);
    return response;
  } catch (error) {
    console.error('Error on upload files ', error);

    return null;
  }
}
